import React, { useCallback } from 'react';

import CustomsItemsCount from './customs-items-count';
import messages from './messages';
import { CustomsErrorBadge } from '../../../../../components/customs-declarations/components/error-badge';
import { CustomsProvider } from '../../../../../components/customs-declarations/context/context';
import CustomsDeclarationsWidget from '../../../../../components/customs-declarations/customs-declarations-widget';
import { ComponentStates } from '../../../../../enums/component-states';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import { useFulfillmentPlanIdContext } from '../../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useFulfillmentRestrictionsByFPId } from '../../../../../hooks/entities/selectors/fulfillment-plans';
import useCustomsIsReadOnly from '../../../hooks/use-customs-read-only';
import useSellerDefaultsForCustoms from '../../../hooks/use-seller-defaults-for-customs';
import ExpanderSection from '../../../tab-section/expander-section/expander-section';
import SectionTitle from '../../../tab-section/title';
import { OrderDetailsSectionId } from '../../order-details-expanded-section-provider';

import { CarrierDocumentWarningBadge } from '../customs-documents/carrier-document-warning-badge';
import { CustomsDocumentsSection } from '../customs-documents/customs-documents-section';
import style from './customs-information.module.less';

const CustomsInformation = () => {
  const fulfillmentPlanId = useFulfillmentPlanIdContext();
  const { customs, needsCustoms, onCustomsChange } = useFulfillmentPlannerContext();
  const { defaultCountryCode, defaultCurrencyCode, defaultWeightUnit } = useSellerDefaultsForCustoms();
  const uiRestrictions = useFulfillmentRestrictionsByFPId(fulfillmentPlanId);
  const readOnly = useCustomsIsReadOnly(fulfillmentPlanId);
  const hideCustomsDeclarations = uiRestrictions?.labelConfiguration?.customs?.componentState === ComponentStates.Hidden;
  const { customsItems = [] } = customs;

  const onSave = useCallback(
    (updatedCustoms) => {
      onCustomsChange(updatedCustoms, needsCustoms);
    },
    [onCustomsChange, needsCustoms],
  );

  if (hideCustomsDeclarations || !needsCustoms || !customsItems.length) {
    return null;
  }

  return (
    <CustomsProvider
      customs={customs}
      defaultCountryCode={defaultCountryCode}
      defaultCurrencyCode={defaultCurrencyCode}
      defaultWeightUnit={defaultWeightUnit}
      fulfillmentPlanId={fulfillmentPlanId}
      saveCustoms={onSave}
    >
      <ExpanderSection
        ariaLabelMessageDescriptor={messages.sectionAriaLabel}
        contentClassName={style['customs-information-section-content']}
        headerContent={
          <>
            <SectionTitle messageDescriptor={messages.customsHeader} />
            <CustomsItemsCount
              customsItemsLength={customsItems.length}
              description={customsItems?.[0].description}
              fulfillmentPlanItemId={customsItems?.[0].fulfillmentPlanId}
              harmonizedTariffCode={customsItems?.[0].harmonizedTariffCode}
              unitPrice={customsItems?.[0].unitPrice.value}
            />
            <div className={style['error-badge']}>{!readOnly && <CustomsErrorBadge />}</div>
            <div className={style['warning-badge']}>
              <CarrierDocumentWarningBadge fulfillmentPlanId={fulfillmentPlanId} />
            </div>
          </>
        }
        sectionContent={
          <>
            <CustomsDeclarationsWidget fulfillmentPlanId={fulfillmentPlanId} readOnly={readOnly} />
            <hr className={style['section-divider']} />
            <CustomsDocumentsSection />
          </>
        }
        sectionId={OrderDetailsSectionId.CustomsDeclarations}
      />
    </CustomsProvider>
  );
};

export default CustomsInformation;
